import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { SegmentedControl, SimpleGrid, Text } from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";

import { DATE_INPUT_FORMAT, toDatePickerFieldProps } from "@prostocheck/controls-web";
import { Card } from "@prostocheck/ui";

import {
  DATE_FROM_FIELD_ID,
  DATE_TO_FIELD_ID,
} from "../../cash-register.constants";
import { toReceiptTypeOptions } from "../../cash-register.utils";
import { TRegisterRangeField } from "../../hooks";
import { IFiltersPanelProps } from "./filters-panel.models";

/**
 * The dates are not just a filter — they are the query for both the
 * Z-reports and the receipts below: a change here re-fetches both. Times and
 * the type only narrow the receipts that came back and cost nothing.
 */
export const FiltersPanel = ({ form }: IFiltersPanelProps) => {
  const { t } = useTranslation();
  const filters = form.values;

  // A range is two fields, so a change to either re-runs the whole form: the
  // message has to leave the field the user has just put right.
  const setBound = (field: TRegisterRangeField, value: string) => {
    form.setFieldValue(field, value);
    form.validate();
  };

  const toTimeHandler =
    (field: TRegisterRangeField) => (event: ChangeEvent<HTMLInputElement>) =>
      setBound(field, event.currentTarget.value);

  return (
    <Card className="mb-6">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <DatePickerInput
          {...toDatePickerFieldProps(DATE_FROM_FIELD_ID)}
          label={t("receipts.dateFrom")}
          valueFormat={DATE_INPUT_FORMAT}
          value={filters.dateFrom}
          error={form.errors.dateFrom}
          onChange={value => setBound("dateFrom", value ?? "")}
        />
        <DatePickerInput
          {...toDatePickerFieldProps(DATE_TO_FIELD_ID)}
          label={t("receipts.dateTo")}
          valueFormat={DATE_INPUT_FORMAT}
          value={filters.dateTo}
          error={form.errors.dateTo}
          onChange={value => setBound("dateTo", value ?? "")}
        />
        <TimeInput
          label={t("receipts.timeFrom")}
          value={filters.timeFrom}
          error={form.errors.timeFrom}
          onChange={toTimeHandler("timeFrom")}
        />
        <TimeInput
          label={t("receipts.timeTo")}
          value={filters.timeTo}
          error={form.errors.timeTo}
          onChange={toTimeHandler("timeTo")}
        />
      </SimpleGrid>

      <div className="mt-4 border-t border-ink-900/8 pt-4">
        <SegmentedControl
          fullWidth
          data={toReceiptTypeOptions(t)}
          {...form.getInputProps("type")}
        />
      </div>

      <Text size="xs" c="dimmed" mt="md">
        {t("receipts.rangeHint")}
      </Text>
    </Card>
  );
};
